import { Stack } from '@mantine/core';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import React, { useState, useEffect } from 'react';
import ModalFilterSchoolManagement from './ModalFilterSchoolManagement';
import { columns as COLUMN_SCHOOL_MANAGEMENT } from './schema';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { useSchoolManagementStore } from 'store/schoolManagement';
import { SchoolData } from 'types/dacum/schoolManagement';
import ModalDelete from 'modules/pages/lcms/courses/ModalDelete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schoolService } from 'services/dacum/school.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

interface SchoolParams {
  search?: string;
  status?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const TableSchoolManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [opened, { open, close }] = useDisclosure(false);
  const [openedModalDelete, { open: openModalDelete, close: closeModalDelete }] =
    useDisclosure(false);
  const [columns, setColumns] = useState(COLUMN_SCHOOL_MANAGEMENT);
  const { formData, setMode, setFormData, reset } = useSchoolManagementStore();
  const [selectedRow, setSelectedRow] = useState<SchoolData | null>(null);
  const [params, setParams] = useState<SchoolParams>({});

  const search = searchParams.get('search');
  const status = searchParams.get('status');
  const sortBy = searchParams.get('sortBy');
  const sortOrder = searchParams.get('sortOrder');
  const pageNumber = searchParams.get('page');
  const pageSize = searchParams.get('size');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const { data, isLoading } = useQuery({
    queryKey: [
      'schoolCompetency',
      search,
      status,
      sortBy,
      sortOrder,
      pageNumber,
      pageSize,
      startDate,
      endDate
    ],
    queryFn: () =>
      schoolService.getSchoolCompetency({
        search: search ?? undefined,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
        status: status ?? undefined,
        sortBy: sortBy ?? undefined,
        sortOrder: (sortOrder as 'asc' | 'desc') ?? 'asc',
        pageNumber: Number(pageNumber) || 1,
        pageSize: Number(pageSize) || 10
      })
  });

  const deleteSchool = useMutation({
    mutationFn: schoolService.deleteSchool,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schoolCompetency'] });
      closeModalDelete();
      setSelectedRow(null);
      showSuccessNotification({
        title: 'Success',
        message: 'School deleted successfully'
      });
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Error when delete school',
        message: error.message
      });
    }
  });

  useEffect(() => {
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        if (value instanceof Date) {
          urlParams.set(key, value.toISOString());
        } else {
          urlParams.set(key, value.toString());
        }
      }
    });
    navigate(`${location.pathname}?${urlParams.toString()}`);
  }, [params, navigate, location.pathname]);

  const handleFilter = (data: SchoolParams) => {
    close();
    setParams(data);
  };

  const handleChangeSize = (size: number) =>
    setParams((prevState) => {
      return { ...prevState, size };
    });

  const handleChangePage = (page: number) =>
    setParams((prevState) => {
      return { ...prevState, page };
    });

  const onDelete = () => {
    return (
      selectedRow &&
      deleteSchool.mutate({
        msSchoolCompetencyManagementID: selectedRow.msSchoolCompetencyManagementID
      })
    );
  };

  const handleEdit = (row: SchoolData) => {
    reset();
    setMode('edit');
    setFormData(row);
    navigate(`${pathnames.schoolManagement}/edit/${row.msSchoolCompetencyManagementID}`);
  };

  const handleDelete = (row: SchoolData) => {
    setSelectedRow(row);
    openModalDelete();
  };

  const handleView = (row: SchoolData) => {
    setMode('view');
    setFormData(row);
    navigate(`${pathnames.schoolManagement}/${row.msSchoolCompetencyManagementID}`);
  };

  return (
    <>
      <Card>
        <Stack>
          <Toolbar
            columns={columns}
            onFilter={() => open()}
            showFilterColumn
            setColumns={setColumns}
            createButtonText='New School'
            buttonCreateLeftSection={<IconPlus />}
            onCreate={() => {
              reset();
              navigate(pathnames.createSchoolManagement);
            }}
          />

          <DataTables
            columns={columns}
            data={data?.data || []}
            loading={isLoading}
            totalData={data?.totalRecords}
            currentPage={Number(params?.page)}
            currentSize={Number(params?.size)}
            withBulkData
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleView={handleView}
            handleChangeSize={handleChangeSize}
            handleChangePage={handleChangePage}
            emptyMessageTitle='No Data Found'
            emptyMessageDescription='Please try again later'
          />
        </Stack>
      </Card>

      <ModalFilterSchoolManagement
        defaultValue={params}
        onClose={() => close()}
        onFilter={handleFilter}
        opened={opened}
      />

      <ModalDelete
        title='Delete Data'
        description={`You are about to delete school ${selectedRow?.schoolName || formData?.schoolName}. Are you sure to delete it?`}
        labelConfirm='Delete'
        onClose={closeModalDelete}
        onConfirm={onDelete}
        opened={openedModalDelete}
        isLoading={deleteSchool.isPending}
      />
    </>
  );
};

export default TableSchoolManagement;
